import { CardBody, CardHeader, CardHeaderActions, Icon, Id } from "@dgs/core";

import {
	DateArg,
	StartOfWeekOptions,
	addWeeks,
	areIntervalsOverlapping,
	differenceInDays,
	endOfWeek,
	format,
	isAfter,
	isBefore,
	startOfWeek,
} from "date-fns";
import styled, { css } from "styled-components";
import { ParticipationStatusIcon } from "../events/eventUsers/ParticipationStatusIcon";
import { EventParticipationStatus } from "../types/event";

const LANE_HEIGHT = 24;
const LANE_SPACING = 2;

export interface ICalendarEvent {
	id: Id;
	start: string;
	end: string;
	name: string;
	isHighlight?: boolean;
	link?: string;
	color?: string | null;
	type: "event" | "holiday";
	participationStatus: EventParticipationStatus;
}

export const CalendarCardBody = styled(CardBody)`
	padding: 0;
`;

export const CalendarRow = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	align-items: center;
	min-height: 2em;
	border-bottom: 1px solid #ccc;
	font-size: 12px;
	font-weight: bold;
	color: #777;
	grid-column: 2;
`;

export const CalendarCardHeader = styled(CardHeader)`
	display: grid;
	grid-template-rows: repeat(3, min-content);
	grid-template-columns: 1fr 1fr 1fr;
	grid-auto-flow: column;
	gap: ${({ theme }) => theme.spacing(2)};
	align-items: start;
	justify-items: start;

	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-rows: min-content min-content min-content;
	}

	${({ theme }) => theme.breakpoints.desktop} {
		align-items: center;
		grid-template-rows: min-content min-content min-content;
	}

	${({ theme }) => theme.breakpoints.largeDesktop} {
		grid-template-rows: 1fr;
		justify-items: inherit;
	}
`;

export const HeaderCalendarRow = styled(CalendarRow)`
	justify-content: center;
	text-align: center;
	font-size: 14px;
	font-weight: bold;
	color: inherit;
	grid-column: 2;
`;

export const CalendarHeaderActions = styled(CardHeaderActions)`
	justify-content: end;
`;

export const HeaderCalendarRowContent = styled.div`
	padding: ${({ theme }) => theme.spacing(2)};
`;

export const GridContainer = styled.div`
	position: relative;
`;

export const Grid = styled.div<{ $showCalendarWeeks: boolean; $maxLanes: number }>`
	display: grid;
	grid-template-columns: ${({ $showCalendarWeeks }) => ($showCalendarWeeks ? "42px" : "")} repeat(7, 1fr);
	grid-auto-rows: max(${({ $maxLanes }) => $maxLanes * (LANE_HEIGHT + LANE_SPACING * 2) + 18}px, 150px);
	background-color: ${({ theme }) => theme.colors.card.background.outline};
	gap: 1px;
`;

export const LaneGrid = styled(Grid)`
	position: absolute;
	pointer-events: none;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: transparent;
`;

export const DayCell = styled.div<{ $isDifferentMonth: boolean; $isCurrentDay: boolean }>`
	color: ${({ theme }) => theme.colors.card.background.text};
	background-color: ${({ theme }) => theme.colors.card.background.background};
	padding: ${({ theme }) => theme.spacing(1)};
	${({ $isDifferentMonth, theme }) =>
		$isDifferentMonth &&
		css`
			color: ${theme.colors.palette.grey[60]};
		`};
	${({ $isCurrentDay, theme }) =>
		$isCurrentDay &&
		css`
			background-color: ${theme.colors.palette.grey[10]};
		`};
`;

export const LaneEvent = styled.div<{ $lane: number; $row: number; $column: string }>`
	pointer-events: all;
	display: grid;
	padding: 4px;
	margin-top: ${({ $lane }) => LANE_HEIGHT + $lane * (LANE_HEIGHT + LANE_SPACING)}px;
	align-content: start;
	grid-row: ${({ $row }) => $row};
	grid-column: ${({ $column }) => $column};
	overflow: hidden;
`;

export const EventGroup = styled.div<{ $color?: string | null; $isHoliday: boolean }>`
	background: ${({ theme, $color }) => $color ?? theme.colors.palette.primary.main.base};
	color: ${({ theme, $color }) =>
		$color ? getContrastBlackOrWhite($color) : theme.colors.palette.primary.main.contrast};
	cursor: pointer;

	${({ $isHoliday, theme }) =>
		$isHoliday &&
		css`
			background: transparent;
			color: ${theme.colors.card.background.text};
			cursor: default;
		`};
	padding: 4px;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	font-size: 12px;
	line-height: ${LANE_HEIGHT - 8}px;
	${({ theme }) => theme.borderRadius.medium};
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(1)};
	z-index: 1;
`;

const laneIconCss = css<{ $color?: string | null }>`
	width: ${LANE_HEIGHT / 2}px;
	height: ${LANE_HEIGHT / 2}px;
	color: ${({ theme, $color }) =>
		$color ? getContrastBlackOrWhite($color) : theme.colors.palette.primary.main.contrast};
`;

export const LaneIcon = styled(Icon)<{ $color?: string | null }>`
	${laneIconCss};
`;

export const HolidayIcon = styled(Icon)`
	width: ${LANE_HEIGHT / 2}px;
	height: ${LANE_HEIGHT / 2}px;
	color: ${({ theme }) => theme.colors.card.background.text};
`;

export const LaneParticipationStatusIcon = styled(ParticipationStatusIcon)<{ $color?: string | null }>`
	${laneIconCss};
`;

export const EventName = styled.span`
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const HeaderGrid = styled(Grid)`
	grid-auto-rows: inherit;
	text-align: center;
	border-bottom: 1px solid ${({ theme }) => theme.colors.card.background.outline};
	gap: 0;

	> * {
		background-color: ${({ theme }) => theme.colors.card.background.background};
	}
`;

export const Weeks = styled.div<{ $end: number }>`
	display: grid;
	grid-row: 1 / ${({ $end }) => $end};
	gap: 1px;
	align-content: stretch;
`;

export const WeekIndicator = styled.div`
	text-align: center;
	color: ${({ theme }) => theme.colors.palette.grey[90]};
	padding: ${({ theme }) => theme.spacing(1)};
	background-color: ${({ theme }) => theme.colors.palette.grey[10]};
`;

const getContrastBlackOrWhite = (hex: string) => {
	if (hex.indexOf("#") === 0) {
		hex = hex.slice(1);
	}
	// convert 3-digit hex to 6-digits.
	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	}
	if (hex.length !== 6) {
		throw new Error("Invalid HEX color.");
	}
	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);

	// https://stackoverflow.com/a/3943023/112731
	return r * 0.299 + g * 0.587 + b * 0.114 > 150 ? "#000000" : "#FFFFFF";
};

const findAvailableLane = (event: ICalendarEvent, lanes: Array<ICalendarEvent[]>, assignedLanes: Map<Id, number>) => {
	const eventId = event.id;

	if (assignedLanes.has(eventId)) {
		const existingLane = assignedLanes.get(eventId) as number;
		lanes[existingLane].push(event);
		return existingLane;
	}

	for (let lane = 0; lane < lanes.length; lane++) {
		const hasConflict = lanes[lane].some((existingEvent) =>
			areIntervalsOverlapping(event, existingEvent, { inclusive: true }),
		);

		if (!hasConflict) {
			lanes[lane].push(event);
			assignedLanes.set(eventId, lane);
			return lane;
		}
	}

	lanes.push([event]);
	const newLane = lanes.length - 1;
	assignedLanes.set(eventId, newLane);
	return newLane;
};

export const getWeekCount = (start: DateArg<Date>, end: DateArg<Date>, weekOptions: StartOfWeekOptions<Date>) =>
	Math.ceil(
		(differenceInDays(end, start, weekOptions) +
			differenceInDays(start, startOfWeek(start, weekOptions), weekOptions) +
			1) /
			7,
	);

export const groupEventsIntoLanes = <T extends ICalendarEvent>(
	relativeStartDate: Date,
	relativeEndDate: Date,
	events: T[],
	weekOptions: StartOfWeekOptions<Date>,
	showCalendarWeeks: boolean,
) => {
	const lanes: T[][] = [];
	const assignedLanes = new Map<Id, number>();

	return events
		.sort((eventA, eventB) => {
			const durationA = differenceInDays(eventA.end, eventA.start, weekOptions);
			const durationB = differenceInDays(eventB.end, eventB.start, weekOptions);

			if (durationA === durationB) {
				return new Date(eventA.start).getTime() - new Date(eventB.start).getTime();
			}

			return durationB - durationA;
		})
		.reduce((eventBlocks, event) => {
			const diffInDays = differenceInDays(new Date(event.end), new Date(event.start), weekOptions);

			if (diffInDays === 0) {
				return [
					...eventBlocks,
					{
						...event,
						start: format(event.start, "yyyy-MM-dd", { weekStartsOn: weekOptions.weekStartsOn }),
						end: format(event.end, "yyyy-MM-dd", { weekStartsOn: weekOptions.weekStartsOn }),
					},
				];
			}

			const groupCount = getWeekCount(event.start, event.end, weekOptions);
			const groups = [];
			let date = new Date(event.start);

			for (let i = 0; i < groupCount; i++) {
				let start = startOfWeek(date, weekOptions);

				if (i === 0) {
					start = new Date(event.start);
				}

				let end = endOfWeek(start, weekOptions);
				if (i === groupCount - 1) {
					end = new Date(event.end);
				}

				date = addWeeks(date, 1, weekOptions);

				if (isAfter(start, relativeEndDate)) {
					continue;
				}
				if (isBefore(end, relativeStartDate)) {
					continue;
				}

				groups.push({
					...event,
					start: format(start, "yyyy-MM-dd", { weekStartsOn: weekOptions.weekStartsOn }),
					end: format(end, "yyyy-MM-dd", { weekStartsOn: weekOptions.weekStartsOn }),
				});
			}

			return [...eventBlocks, ...groups];
		}, [] as T[])
		.map((group: T) => {
			const lane = findAvailableLane(group, lanes, assignedLanes);
			const duration = differenceInDays(group.end, group.start, weekOptions) + 1;

			return {
				...group,
				lane,
				row: Math.ceil((differenceInDays(group.start, relativeStartDate, weekOptions) + 1) / 7),
				column: `${(showCalendarWeeks ? 2 : 1) + differenceInDays(group.start, startOfWeek(group.start, weekOptions), weekOptions)} / span ${duration}`,
			};
		});
};
